import type { ActionReturnType } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { PASSCODE_IDB_STORE } from '../../../util/browser/idb';
import { toCredentialRequestOptions } from '../../../util/browser/passkeys';
import {
  IS_WEBAUTHN_SUPPORTED,
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import {
  ACCOUNT_SLOT,
  getAccountsInfo,
  getAccountSlotUrl,
  getBotLoginRedirectSlot,
} from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredBotSession,
  loadStoredBotToken,
  loadStoredSession,
  loadSlotSession,
  storeBotSessionInfo,
  storeSession,
} from '../../../util/sessions';
import {
  RASITECH_BOT_AUTH_TOKEN_KEY,
  RASITECH_BOT_SESSION_MARKER_KEY,
  RASITECH_BOT_UI_ONLY_KEY,
  clearBotAuthSessionKeys,
} from '../../../util/modFeatures';
import { safeSessionStorageGet, safeSessionStorageRemove, safeSessionStorageSet } from '../../../util/safeStorage';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { updateAuth } from '../../reducers/auth';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const isTestServer = global.config?.isTestServer;
  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo)
    .filter((info) => info.isTest === isTestServer)
    .map(({ userId }) => userId)
    .filter(Boolean);

  const botSession = loadStoredBotSession();
  const pendingBotToken = safeSessionStorageGet(RASITECH_BOT_AUTH_TOKEN_KEY);
  const botToken = loadStoredBotToken() || pendingBotToken;
  const sessionData = botSession || loadStoredSession();

  if (botToken || pendingBotToken) {
    global = updateAuth(global, {
      errorKey: undefined,
    });
  }

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData,
    botAuthToken: pendingBotToken,
    botToken,
    isBotSession: Boolean(botSession || loadSlotSession(ACCOUNT_SLOT)?.isBot),
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
    hasPasskeySupport: IS_WEBAUTHN_SUPPORTED,
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));

  return global;
});

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload;

  void callApi('provideAuthCode', code);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload;

  void callApi('provideAuthPassword', password);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('loginWithPasskey', async (global, actions, payload): Promise<void> => {
  const passkeyOption = global.auth.passkeyOption;
  if (!passkeyOption) return;

  const credential = await navigator.credentials.get(toCredentialRequestOptions(passkeyOption)).catch((e: unknown) => {
    actions.showNotification({
      message: {
        key: 'PasskeyLoginError',
      },
      tabId: getCurrentTabId(),
    });
  });
  if (!credential) return;

  const publicKeyCredential = credential as PublicKeyCredential;
  callApi('restartAuthWithPasskey', publicKeyCredential.toJSON() as AuthenticationResponseJSON);
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  const userId = bot?.id ?? global.currentUserId;
  if (!userId) return;

  actions.loadFullUser({ userId });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload;

  void callApi('provideAuthRegistration', { firstName, lastName });

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  clearBotAuthSessionKeys();
  void callApi('restartAuth');

  return updateAuth(global, {
    errorKey: undefined,
  });
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  clearBotAuthSessionKeys();
  void callApi('restartAuthWithQr');

  return updateAuth(global, {
    isLoadingQrCode: true,
    errorKey: undefined,
  });
});

addActionHandler('goToAuthBotToken', (global): ActionReturnType => {
  safeSessionStorageSet(RASITECH_BOT_UI_ONLY_KEY, '1');

  return updateAuth(global, {
    state: 'authorizationStateWaitBotToken',
    errorKey: undefined,
    isLoading: false,
  });
});

addActionHandler('leaveAuthBotToken', async (global, actions): Promise<void> => {
  const isUiOnly = safeSessionStorageGet(RASITECH_BOT_UI_ONLY_KEY) === '1';
  clearBotAuthSessionKeys();

  const isMobile = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';

  global = updateAuth(global, {
    state: isMobile ? 'authorizationStateWaitPhoneNumber' : 'authorizationStateWaitQrCode',
    isLoading: false,
    isLoadingQrCode: isUiOnly ? false : !isMobile,
    errorKey: undefined,
  });
  setGlobal(global);

  if (isUiOnly) {
    return;
  }

  void callApi('cancelAuthBotToken');

  try {
    await Promise.race([callApi('destroy', true, false), pause(3000)]);
  } catch {
    // Client may not be initialized yet on the auth screen.
  }

  actions.initApi();
});

addActionHandler('loginWithBotToken', async (global, actions, payload): Promise<void> => {
  const token = payload.token.trim();
  if (!token) {
    return;
  }

  safeSessionStorageSet(RASITECH_BOT_AUTH_TOKEN_KEY, token);
  safeSessionStorageSet(RASITECH_BOT_SESSION_MARKER_KEY, '1');

  global = updateAuth(global, {
    rememberMe: true,
  });
  setGlobal(global);

  const redirectSlot = getBotLoginRedirectSlot();
  if (redirectSlot !== undefined && redirectSlot !== (ACCOUNT_SLOT || 1)) {
    window.location.href = getAccountSlotUrl(redirectSlot, true);
    return;
  }

  if (global.auth.state === 'authorizationStateWaitBotToken') {
    const isUiOnly = safeSessionStorageGet(RASITECH_BOT_UI_ONLY_KEY) === '1';
    if (!isUiOnly) {
      void callApi('provideAuthBotToken', token);
      global = updateAuth(global, {
        isLoading: true,
        errorKey: undefined,
      });
      setGlobal(global);
      return;
    }

    safeSessionStorageRemove(RASITECH_BOT_UI_ONLY_KEY);
  }

  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  try {
    await Promise.race([callApi('destroy', true, false), pause(3000)]);
  } catch {
    // Client may not be initialized yet on the auth screen.
  }

  clearStoredSession(ACCOUNT_SLOT);

  global = getGlobal();
  global = updateAuth(global, {
    state: 'authorizationStateWaitBotToken',
    isLoading: true,
    errorKey: undefined,
  });
  setGlobal(global);

  actions.initApi();
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);

    const pendingToken = safeSessionStorageGet(RASITECH_BOT_AUTH_TOKEN_KEY);
    const isBotLogin = Boolean(
      safeSessionStorageGet(RASITECH_BOT_SESSION_MARKER_KEY)
      || loadSlotSession(ACCOUNT_SLOT)?.isBot,
    );
    if (isBotLogin) {
      storeAccountData(ACCOUNT_SLOT, { isBot: true });
      storeBotSessionInfo(pendingToken);
    }
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  const skipLogOut = Boolean(global.currentUserId && global.users.byId[global.currentUserId]?.type === 'userTypeBot')
    || Boolean(loadStoredBotSession());

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy', skipLogOut), pause(3000)]);
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  clearBotAuthSessionKeys();
  void import('../../../rasitech/botMessagerSupport').then(({ clearBotMessagerRequestCache }) => {
    clearBotMessagerRequestCache();
  });

  actions.reset();

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);
  clearEncryptedSession();
  clearBotAuthSessionKeys();
  void import('../../../rasitech/botMessagerSupport').then(({ clearBotMessagerRequestCache }) => {
    clearBotMessagerRequestCache();
  });

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  // Check if there are any accounts left
  const accounts = getAccountsInfo();
  if (!Object.values(accounts).length) {
    PASSCODE_IDB_STORE.clear();
    removeSharedStateFromCache();
  }

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = updateAuth(global, {
    nearestCountry: authNearestCountry,
  });
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, payload): ActionReturnType => {
  const { token } = payload;
  return {
    ...global,
    push: {
      deviceToken: token,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...loadStoredSession(), userId: global.currentUserId });
  const globalJson = serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
